import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import publicApi from "@/lib/publicApi";
import { MailX, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

const H = { fontFamily: "'Cabinet Grotesk', sans-serif" };

const Card = ({ children, testid }) => (
  <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-5 py-16" data-testid={testid}>
    <div className="w-full max-w-md bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-8 shadow-[0_4px_24px_rgba(0,0,0,0.04)]">
      {children}
    </div>
  </div>
);

export default function UnsubscribePage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState(null); // subscribed | unsubscribed | invalid | error
  const [info, setInfo] = useState({});
  const [working, setWorking] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await publicApi.get(`/public/marketing/unsubscribe/${token}`);
        if (cancelled) return;
        setInfo(res.data);
        setState(res.data.status);
      } catch {
        if (!cancelled) setState("error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const confirm = async () => {
    setWorking(true);
    try {
      const res = await publicApi.post(`/public/marketing/unsubscribe/${token}`);
      setInfo({ ...info, ...res.data });
      setState("unsubscribed");
    } catch {
      setState("error");
    } finally {
      setWorking(false);
    }
  };

  if (loading) {
    return (
      <Card testid="unsubscribe-loading">
        <div className="flex items-center gap-3 text-sm text-[#757675]">
          <Loader2 className="w-5 h-5 animate-spin text-[#205B5A]" /> Loading...
        </div>
      </Card>
    );
  }

  if (state === "invalid" || state === "error") {
    return (
      <Card testid="unsubscribe-invalid">
        <div className="w-12 h-12 rounded-xl bg-red-50 text-red-500 flex items-center justify-center mb-5">
          <AlertCircle className="w-6 h-6" />
        </div>
        <h1 className="text-xl md:text-2xl text-[#1A1B1A] mb-3" style={H}>This unsubscribe link isn't valid</h1>
        <p className="text-sm text-[#4A4B4A] leading-relaxed">
          The link may have expired or been mistyped. Please contact your clinic if you'd like to stop receiving
          marketing emails.
        </p>
      </Card>
    );
  }

  if (state === "unsubscribed") {
    return (
      <Card testid="unsubscribe-success">
        <div className="w-12 h-12 rounded-xl bg-[#205B5A]/10 text-[#205B5A] flex items-center justify-center mb-5">
          <CheckCircle2 className="w-6 h-6" />
        </div>
        <h1 className="text-xl md:text-2xl text-[#1A1B1A] mb-3" style={H}>
          You have been unsubscribed from marketing emails.
        </h1>
        <p className="text-sm text-[#4A4B4A] leading-relaxed">
          You will no longer receive marketing emails from {info.clinic_name || "your clinic"}. You will still receive
          essential emails about your care, such as appointment confirmations, reminders and forms.
        </p>
      </Card>
    );
  }

  return (
    <Card testid="unsubscribe-confirm">
      <div className="w-12 h-12 rounded-xl bg-[#FAF9F6] text-[#205B5A] flex items-center justify-center mb-5">
        <MailX className="w-6 h-6" />
      </div>
      <h1 className="text-xl md:text-2xl text-[#1A1B1A] mb-3" style={H}>Unsubscribe from marketing emails</h1>
      <p className="text-sm text-[#4A4B4A] leading-relaxed mb-6">
        Confirm below and {info.clinic_name || "your clinic"} will stop sending marketing emails to
        {info.email ? ` ${info.email}` : " this address"}. Appointment confirmations, reminders and forms will still
        reach you.
      </p>
      <button
        onClick={confirm}
        disabled={working}
        data-testid="unsubscribe-confirm-btn"
        className="w-full py-3 rounded-xl bg-[#205B5A] text-white text-sm font-medium hover:bg-[#164342] disabled:opacity-60 transition-colors"
      >
        {working ? "Unsubscribing..." : "Unsubscribe me"}
      </button>
    </Card>
  );
}
